import type * as THREE from 'three';

/**
 * Where a mesh crosses the midline: its outline loops as [z, y] points (the mesh's own units), the
 * longest first. Triangle edges crossing the plane give segments, chained end to end (ends matched
 * to within `tolerance`). The plane sits `offset` off the midline (x = offset): a symmetric mesh has
 * vertices exactly at x = 0, which would give zero-length, branching segments.
 */
export function midlineLoops(
  geometry: THREE.BufferGeometry,
  tolerance = 1e-5,
  offset = 2e-4,
): [number, number][][] {
  const position = geometry.getAttribute('position');
  const index = geometry.getIndex();
  const count = index ? index.count : position.count;
  const vertex = (i: number): [number, number, number] => {
    const v = index ? index.getX(i) : i;
    return [position.getX(v) - offset, position.getY(v), position.getZ(v)];
  };
  const segments: [[number, number], [number, number]][] = [];
  for (let i = 0; i + 2 < count; i += 3) {
    const tri = [vertex(i), vertex(i + 1), vertex(i + 2)];
    const hits: [number, number][] = [];
    for (let e = 0; e < 3; e++) {
      const a = tri[e];
      const b = tri[(e + 1) % 3];
      if (!a || !b || a[0] === b[0]) continue;
      if ((a[0] < 0 && b[0] < 0) || (a[0] > 0 && b[0] > 0)) continue;
      const t = a[0] / (a[0] - b[0]);
      hits.push([a[2] + (b[2] - a[2]) * t, a[1] + (b[1] - a[1]) * t]);
    }
    const [p, q] = hits;
    if (p && q) segments.push([p, q]);
  }
  // Chain: index segment ends by a rounded key.
  const key = ([z, y]: [number, number]) =>
    `${String(Math.round(z / tolerance))},${String(Math.round(y / tolerance))}`;
  const byEnd = new Map<string, number[]>();
  segments.forEach(([p, q], i) => {
    for (const end of [p, q]) {
      const k = key(end);
      byEnd.set(k, [...(byEnd.get(k) ?? []), i]);
    }
  });
  const used = new Set<number>();
  const loops: [number, number][][] = [];
  for (let s = 0; s < segments.length; s++) {
    if (used.has(s)) continue;
    used.add(s);
    const first = segments[s];
    if (!first) continue;
    const loop: [number, number][] = [first[0], first[1]];
    for (;;) {
      const end = loop[loop.length - 1];
      if (!end) break;
      const next = (byEnd.get(key(end)) ?? []).find((i) => !used.has(i));
      if (next === undefined) break;
      used.add(next);
      const seg = segments[next];
      if (!seg) break;
      loop.push(key(seg[0]) === key(end) ? seg[1] : seg[0]);
    }
    if (loop.length > 8) loops.push(loop);
  }
  return loops.sort((a, b) => b.length - a.length);
}

/**
 * One closed outline from the open chains of several meshes cut at the midline (a head whose mesh
 * is open at the neck and around the mouth): chains of at least `minPoints`, ordered by their angle
 * around `centre` ([z, y]), each turned to run the same way round, joined end to start.
 */
export function joinedOutline(
  chains: [number, number][][],
  centre: [number, number],
  minPoints = 20,
): [number, number][] {
  const angle = ([z, y]: [number, number]) => Math.atan2(y - centre[1], z - centre[0]);
  const unwrap = (a: number, ref: number) => {
    let d = a - ref;
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    return ref + d;
  };
  const kept = chains
    .filter((c) => c.length >= minPoints)
    .map((c) => {
      const first = c[0] ?? centre;
      const last = c[c.length - 1] ?? centre;
      // Counterclockwise: the angle grows from the first point to the last.
      const a0 = angle(first);
      const forward = unwrap(angle(last), a0) >= a0;
      const run = forward ? c : [...c].reverse();
      return { run, at: angle(run[0] ?? centre) };
    })
    .sort((a, b) => a.at - b.at);
  return kept.flatMap((k) => k.run);
}
